import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { AppLocalStorage } from '../utils/local-storage.util';
import { SessionService } from '../session/session.service';
import { Store } from '../store/store';
import { GetEmployeRelationLoadedState, GetEmployeRelationLoadingErrorState, GetEmployeRelationLoadingState } from './declaration-prompt.state';
import { environment } from '../../../environments/environment';
import { TokenService } from '../token/token.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

export interface DeclarationPromptData {
  shouldShow: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DeclarationPromptService extends Store.AbstractService {
  private profileSubscription!: Subscription;
  private promptSubject = new BehaviorSubject<DeclarationPromptData | null>(null);
  private draftNoticeSubject = new BehaviorSubject<boolean>(false);
  // null until resolved; 'not-applicable' for employees outside the declaration policy.
  private declarationStatusSubject = new BehaviorSubject<string | null>(null);
  private isCheckingPromptEligibility = false;
  private draftRedirected = false;

  public readonly prompt$ = this.promptSubject.asObservable();
  public readonly draftNotice$ = this.draftNoticeSubject.asObservable();
  public readonly declarationStatus$ = this.declarationStatusSubject.asObservable();

  constructor(private sessionService: SessionService, private http: HttpClient,
    private tokenService: TokenService, private router: Router) {
    super();
    this.initializePromptCheck();
  }

  private initializePromptCheck(): void {
    this.profileSubscription = this.sessionService.profileDataLoaded$.subscribe(
      (profileData) => {
        if (profileData) {
          void this.checkPromptEligibility();
        }
      }
    );
  }

  async checkPromptEligibility(): Promise<void> {
    if (this.isCheckingPromptEligibility || !AppLocalStorage.get('PROFILE_DATA', 'PROFILE_DATA')) {
      return;
    }

    this.isCheckingPromptEligibility = true;
    try {
      if (!this.isIndianEmployee()) {
        this.declarationStatusSubject.next('not-applicable');
        return;
      }

      const status = this.getDeclarationFormStatus(await this.checkFormDraft());
      this.declarationStatusSubject.next(status || 'none');

      if (status === 'draft' && !this.draftRedirected) {
        this.draftRedirected = true;
        void this.router.navigate(['declaration']);
        this.draftNoticeSubject.next(true);
      }
    } catch (error) {
      console.error('Prompt eligibility check error:', error);
    } finally {
      this.isCheckingPromptEligibility = false;
    }
  }

  dismissDraftNotice(): void {
    this.draftNoticeSubject.next(false);
  }

  openPrompt(): void {
    this.promptSubject.next({ shouldShow: true });
  }

  markDeclarationFormSubmitted(): void {
    this.declarationStatusSubject.next('submitted');
  }

  private isIndianEmployee(): boolean {
    const profileData = AppLocalStorage.get('PROFILE_DATA', 'PROFILE_DATA');
    const nationality = profileData?.nationality || profileData?.profile?.nationality;

    return typeof nationality === 'string' && nationality.trim().toLowerCase() === 'indian';
  }

  async saveRelationshipStatus(hasRelationship: boolean): Promise<any> {
    try {
      const token = await this.tokenService.getAccessToken();
      const headers = new HttpHeaders({
        Authorization: `Bearer ${token}`
      });

      const endpoint = environment.api.declaration.EmployeeRelationsGetStatus.endpoint;
      const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

      return await firstValueFrom(
        this.http.post(
          `${environment.api.core?.base3}${normalizedEndpoint}`,
          { hasRelationship },
          { headers }
        )
      );
    } catch (error) {
      console.error('Save relationship status error:', error);
      throw error;
    }
  }

  async checkFormDraft(): Promise<any> {
    try {
      const token = await this.tokenService.getAccessToken();
      const headers = new HttpHeaders({
        Authorization: `Bearer ${token}`
      });

      return await firstValueFrom(
        this.http.get(
          `${environment.api.core?.base3}/EmployeeRelation/declaration-status`,
          { headers }
        )
      );
    } catch (error) {
      console.error('Check form draft status error:', error);
      throw error;
    }
  }

  // Backward compatibility for existing call sites.
  async checFormDraft(): Promise<any> {
    return this.checkFormDraft();
  }

  getDeclarationFormStatus(response: any): string {
    const status = response?.status || response?.['declaration status'] || response?.declarationStatus ||
      response?.data?.status || response?.data?.['declaration status'] || response?.data?.declarationStatus ||
      response?.result?.status || response?.result?.['declaration status'] || response?.result?.declarationStatus;

    if (typeof status === 'string') {
      return status.trim().toLowerCase();
    }

    return '';
  }

  dismissPrompt(): void {
    // Mark as shown so it doesn't appear again
    AppLocalStorage.set('DECLARATION_PROMPT', 'shown', true);
    this.promptSubject.next(null);
  }

  ngOnDestroy(): void {
    if (this.profileSubscription) {
      this.profileSubscription.unsubscribe();
    }
  }
}
